import { useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, Minus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCustomPackages } from '@/api/domains/custom-packages';
import { useMe } from '@/api/domains/merchant-settings';
import { useBatchCreateShipments, useShipmentBatch } from '@/api/domains/shipments';
import { predefinedTemplatesFor, usePackageTemplates } from '@/api/domains/package-templates';
import { queryKeys } from '@/api/keys';
import type { ShipmentBatchItemStatus } from '@/api/types/shipments';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toApiProblem, toastProblem } from '@/lib/errors';
import { cn } from '@/lib/utils';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderIds: number[];
  /**
   * Destination countries of the selected orders (backend-resolved per order).
   * Predefined sizes are per destination market, so the select offers the
   * union for these markets; a size missing for one order's market fails just
   * that item with a translated reason.
   */
  destinationCountries: (string | null)[];
  onSuccess?: () => void;
};

function ItemIcon({ status, label }: { status: ShipmentBatchItemStatus; label: string }) {
  if (status === 'CREATED') {
    return <Check className="h-4 w-4 text-green-600" aria-label={label} />;
  }
  if (status === 'FAILED') {
    return <X className="h-4 w-4 text-red-600" aria-label={label} />;
  }
  if (status === 'SKIPPED') {
    return <Minus className="h-4 w-4 text-muted-foreground" aria-label={label} />;
  }
  return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label={label} />;
}

export function BatchCreateShipmentsDialog({
  open,
  onOpenChange,
  orderIds,
  destinationCountries,
  onSuccess,
}: Props) {
  const { t } = useTranslation('shipments');
  const tCommon = useTranslation('common').t;
  const qc = useQueryClient();
  const templates = usePackageTemplates();
  const customPackages = useCustomPackages();
  const batch = useBatchCreateShipments();

  const predefined = useMemo(
    () => predefinedTemplatesFor(templates.data, destinationCountries),
    [templates.data, destinationCountries],
  );
  // The merchant's default package size (predefined slug or `custom:{id}`)
  // wins when it still exists; otherwise the predefined default. Orders whose
  // delivery method can't take the package (too big for a locker) fail per
  // item with a translated reason - every option stays selectable here on
  // purpose (mixed batches).
  const defaultPackageSetting = useMe().data?.settings?.default_package_key ?? null;
  const defaultTemplate = useMemo(() => {
    if (defaultPackageSetting) {
      if (predefined.some((tpl) => tpl.key === defaultPackageSetting)) {
        return defaultPackageSetting;
      }
      if (customPackages.data?.some((pkg) => pkg.key === defaultPackageSetting)) {
        return defaultPackageSetting;
      }
    }
    return (
      predefined.find((v) => v.is_default)?.key ??
      predefined[0]?.key ??
      customPackages.data?.[0]?.key ??
      ''
    );
  }, [defaultPackageSetting, predefined, customPackages.data]);
  // Empty until the user picks one; fall back to the default so the form is
  // submittable as soon as templates load (the initial useState can't see the
  // not-yet-loaded default). An explicit pick that stopped existing (custom
  // package deleted mid-session, list refetched) also falls back to the
  // default instead of sending a doomed key.
  const [templateKey, setTemplateKey] = useState<string>('');
  const packageOptionExists = (key: string) =>
    Boolean(
      predefined.some((tpl) => tpl.key === key) ||
      customPackages.data?.some((pkg) => pkg.key === key),
    );
  const noPackagesAvailable =
    !templates.isLoading &&
    !customPackages.isLoading &&
    predefined.length === 0 &&
    (customPackages.data?.length ?? 0) === 0;
  const pickedTemplate = templateKey || defaultTemplate;
  const selectedTemplate =
    pickedTemplate && !packageOptionExists(pickedTemplate) ? defaultTemplate : pickedTemplate;
  const [batchId, setBatchId] = useState<number | null>(null);

  const progress = useShipmentBatch(batchId);
  const data = progress.data;
  const items = data?.items ?? [];
  const started = batchId !== null;
  const isComplete = data?.status === 'COMPLETED';
  // While the job runs the modal is locked shut so progress isn't lost to a stray
  // click; an error also settles it so the user can never get trapped.
  const isProcessing = started && !isComplete && !progress.isError;

  // Refresh the listings once the batch settles so the new shipments show up.
  useEffect(() => {
    if (isComplete) {
      void qc.invalidateQueries({ queryKey: queryKeys.orders.all });
      void qc.invalidateQueries({ queryKey: queryKeys.orderShipments.all });
      void qc.invalidateQueries({ queryKey: queryKeys.shipments.all });
    }
  }, [isComplete, qc]);

  const reset = () => {
    setBatchId(null);
    setTemplateKey('');
    batch.reset();
  };

  const handleSubmit = () => {
    batch.mutate(
      { order_ids: orderIds, package_template_key: selectedTemplate },
      {
        onSuccess: (created) => {
          setBatchId(created.id);
          onSuccess?.();
        },
        onError: (error) => toastProblem(toApiProblem(error)),
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Mid-run: swallow every dismissal (Esc / outside-click / X). The footer
        // Close button is the only way out, and only once the batch has settled.
        if (!next && isProcessing) {
          return;
        }
        if (!next) {
          reset();
        }
        onOpenChange(next);
      }}
    >
      {/* Hide the built-in close X while locked - the guard above already blocks it,
          this just stops it from looking clickable. */}
      <DialogContent className={cn(isProcessing && '[&>button]:hidden')}>
        <DialogHeader>
          <DialogTitle>{started ? t('batch.progress.title') : t('batch.dialogTitle')}</DialogTitle>
          {!started && <DialogDescription>{t('batch.dialogDescription')}</DialogDescription>}
        </DialogHeader>

        {!started ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('batch.packageTemplate')}</Label>
              <Select
                value={selectedTemplate}
                onValueChange={setTemplateKey}
                disabled={templates.isLoading || customPackages.isLoading}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {predefined.length ? (
                    <SelectGroup>
                      {predefined.map((tpl) => (
                        <SelectItem key={tpl.key} value={tpl.key}>
                          {tpl.name} ({tpl.height}×{tpl.width}×{tpl.length}{' '}
                          {tpl.dimension_unit.toLowerCase()}, {tpl.weight}{' '}
                          {tpl.weight_unit.toLowerCase()})
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ) : null}
                  {customPackages.data?.length ? (
                    <>
                      {predefined.length ? <SelectSeparator /> : null}
                      <SelectGroup>
                        <SelectLabel>{t('packageGroups.custom')}</SelectLabel>
                        {customPackages.data.map((pkg) => (
                          <SelectItem key={pkg.key} value={pkg.key}>
                            {pkg.name} ({pkg.height}×{pkg.width}×{pkg.length}{' '}
                            {pkg.dimension_unit.toLowerCase()}, {pkg.weight}{' '}
                            {pkg.weight_unit.toLowerCase()})
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </>
                  ) : null}
                </SelectContent>
              </Select>
              {noPackagesAvailable ? (
                <p className="text-xs text-amber-700">{t('batch.noPackagesHint')}</p>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
              {isComplete
                ? t('batch.progress.summary', {
                    created: data?.created_count ?? 0,
                    failed: data?.failed_count ?? 0,
                  })
                : t('batch.progress.processing', {
                    resolved: data
                      ? data.created_count + data.failed_count + data.skipped_count
                      : 0,
                    total: data?.total_count ?? orderIds.length,
                  })}
            </p>

            <ul className="max-h-72 space-y-2 overflow-y-auto [scrollbar-gutter:stable]">
              {progress.isError && !data && (
                <li className="text-sm text-red-600">{t('batch.progress.loadError')}</li>
              )}
              {items.map((item) => (
                <li key={item.id} className="flex items-start gap-2 text-sm">
                  <span className="mt-0.5 shrink-0">
                    <ItemIcon
                      status={item.status}
                      label={t(`batch.progress.itemStatus.${item.status}`)}
                    />
                  </span>
                  <span className="min-w-0">
                    <span className="font-medium text-black">
                      {item.order_number ?? `#${item.shopify_order_id}`}
                    </span>
                    {item.status === 'FAILED' && item.error_message && (
                      <span className="block text-red-600">{item.error_message}</span>
                    )}
                    {/* A COD order whose shipment lost the COD VAS (settled
                        payment, split, unaccepted currency) must not pass as a
                        plain green check - nothing will be collected. */}
                    {item.status === 'CREATED' && item.cod_missing && (
                      <span className="block text-amber-600">{t('batch.progress.codMissing')}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter>
          {!started ? (
            <>
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={batch.isPending}
              >
                {tCommon('actions.cancel')}
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={batch.isPending || !selectedTemplate || orderIds.length === 0}
              >
                {batch.isPending
                  ? tCommon('actions.saving')
                  : t('batch.submit', { count: orderIds.length })}
              </Button>
            </>
          ) : (
            <Button onClick={() => onOpenChange(false)} disabled={isProcessing}>
              {isProcessing ? t('batch.progress.creating') : tCommon('actions.close')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
